import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePathname, useRouter } from 'expo-router';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useWorkoutTimer } from '~/workout/hooks/useWorkoutTimer';
import { useWorkoutActions } from '~/workout/hooks/useWorkoutActions';
import { useRecordedSessionImport } from '~/health/hooks/useRecordedSessionImport';
import { useConfirmation } from '~/global/hooks/useConfirmation';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import type { WorkoutRecord } from '~/powersync/generated/schema';

export interface WorkoutHeaderActionsProps {
  workout: WorkoutRecord;
}

/**
 * Header controls for an in-progress workout:
 * - a settings/progress toggle that switches between the builder and the
 *   "in progress" overview,
 * - a live timer + stop control that finishes the workout.
 *
 * Rendered as the right-hand side of the shared workout header while the
 * workout is STARTED.
 */
export const WorkoutHeaderActions = ({
  workout,
}: WorkoutHeaderActionsProps) => {
  const { t } = useTranslation('workout');
  const router = useRouter();
  const pathname = usePathname();
  const { stopWorkout, isStopping } = useWorkoutActions();
  const { importForWorkout } = useRecordedSessionImport();
  const { formattedTime } = useWorkoutTimer({
    startedAt: workout.startedAt,
    finishedAt: workout.finishedAt,
  });

  const isOnProgressRoute = pathname.includes('/progress');

  const { dispatchConfirmationDialog } = useConfirmation(
    async (workoutId: string) => {
      await stopWorkout(workoutId);
      // Once, at finish: look for the device-recorded session to attach (#29).
      void importForWorkout(workoutId);
    },
    {
      title: t('stopConfirmation.title'),
      description: t('stopConfirmation.description'),
      confirmText: t('form.buttons.stopWorkout'),
      cancelText: t('form.buttons.cancel'),
    },
  );

  const handleToggleRoute = () => {
    if (!workout.niceId) return;
    if (isOnProgressRoute) {
      router.replace(`/workouts/${workout.niceId}`);
    } else {
      router.replace(`/workouts/${workout.niceId}/progress`);
    }
  };

  return (
    <View className="flex-row items-center gap-3">
      {/* Timer — tapping it stops the workout */}
      <Pressable
        testID="workout-stop-button"
        onPress={() => dispatchConfirmationDialog(workout.id)}
        disabled={isStopping}
        accessibilityRole="button"
        className="flex-row items-center gap-1 rounded-lg bg-surface px-2.5 py-1.5 active:opacity-70 disabled:opacity-50"
      >
        <Icon name="Timer" size={14} className="text-accent" />
        <Text className="text-xs font-medium text-text-primary">
          {formattedTime}
        </Text>
      </Pressable>

      {/* Settings / progress toggle */}
      <Pressable
        onPress={handleToggleRoute}
        accessibilityRole="button"
        className="h-10 w-10 items-center justify-center rounded-full bg-surface active:opacity-70"
      >
        <Icon
          name={isOnProgressRoute ? 'Settings' : 'TrendingUp'}
          size={20}
          className="text-text-secondary"
        />
      </Pressable>
    </View>
  );
};
